import React, { useMemo, useState } from 'react';
import { createMinutes, editMinutes, approveMinutes, sendMinutes, reasonOf } from '../../lib/meetings';
import useRecordingReason from '../recording/useRecordingReason';
import { relative } from '../../lib/when';
import { transcriptWords } from '../../lib/recordings';
import Markdown from '../recording/Markdown';
import { Dialog, GrowingTextarea, Avatar } from '../todos/TodoPeople';
import { Icon } from '../todos/icons';
import { useFeature } from '../../lib/features';

/*
 * THE MINUTES OF A MEETING (Gordon, 22 October).
 *
 * In English, written from the recording's transcript; the organiser reads
 * them FIRST, and nobody else sees a word until they have approved them -
 * unless the meeting was made with "send automatically", in which case the
 * server sends them when they are ready and this panel only shows what went.
 * Minutes stay inside Jarvis unless the organiser ticked "also email minutes
 * to outside guests" when making the meeting; Send here goes to the
 * attendees who are in Jarvis, and to the guests only when that was ticked.
 *
 * Every version is kept. Creating again, or editing, is a new version with a
 * number; the newest is the one on screen, with the earlier ones folded
 * under it. Draft, Approved and Sent are the three states, each a chip whose
 * colour says what kind of news it is, and each step is one button:
 * Create → Edit (a textarea with the rendered page beside it, live) →
 * Approve → Send to attendees (which asks first, and says who will get it).
 *
 * Markdown is drawn through components/recording/Markdown.js, never as HTML.
 */

const STATE = {
  draft: { word: 'Draft', tone: 'quiet' },
  approved: { word: 'Approved', tone: 'good' },
  sent: { word: 'Sent', tone: 'info' }
};

export const MinutesChip = ({ state }) => {
  const s = STATE[state] || STATE.draft;
  return <span className={`mt-chip is-${s.tone}`} data-minutes-state={state || 'draft'}>{s.word}</span>;
};

const byVersion = (a, b) => (Number(b.version) || 0) - (Number(a.version) || 0)
  || (Date.parse(b.created_at || 0) || 0) - (Date.parse(a.created_at || 0) || 0);

/** Who Send will reach: the attendees in Jarvis always, the guests only when the meeting says so. */
export const recipientsOf = (meeting) => {
  const all = Array.isArray(meeting?.attendees) ? meeting.attendees : [];
  const inside = all.filter((a) => a.kind !== 'guest');
  const guests = all.filter((a) => a.kind === 'guest');
  return { inside, guests, guestsGet: Boolean(meeting?.send_minutes) };
};

export default function MinutesPanel({ meeting, recordings = [], minutes = [], onChange, canEdit = true, focusId = null }) {
  const list = useMemo(() => (Array.isArray(minutes) ? minutes : []).slice().sort(byVersion), [minutes]);
  const latest = list[0] || null;
  // The recording minutes are written from: the newest with a transcript.
  const ready = useMemo(() => (Array.isArray(recordings) ? recordings : [])
    .filter((r) => /Ready/.test(transcriptWords(r)))
    .sort((a, b) => (Date.parse(b.started_at || 0) || 0) - (Date.parse(a.started_at || 0) || 0)), [recordings]);
  const [recId, setRecId] = useState(null);
  const source = ready.find((r) => r.id === recId) || ready[0] || null;

  const [prompt, setPrompt] = useState('');
  const [busy, setBusy] = useState(null); // 'create' | 'save' | 'approve' | 'send'
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sent, setSent] = useState(null);
  // Not on the call: the reason first, once a day (3 Nov).
  const [withReason, reasonDialog] = useRecordingReason();
  // A version the bell pointed at (?minutes=<id>) is opened even when it
  // is not the newest: the earlier ones unfold, and that one is unrolled.
  const focusedOld = Boolean(focusId) && list.some((m) => String(m.id) === String(focusId) && m !== latest);
  const [showOld, setShowOld] = useState(focusedOld);

  const replace = (next) => onChange((cur) => {
    const rows = (Array.isArray(cur) ? cur : []).filter((m) => m.id !== next.id);
    return [...rows, next];
  });

  const create = async () => {
    if (!source) return;
    setBusy('create');
    setError(null);
    try {
      const m = await withReason(source.id, () => createMinutes(source.id, prompt.trim()), 'summary');
      onChange((cur) => [...(Array.isArray(cur) ? cur : []), { state: 'draft', ...m, recording_id: m.recording_id || source.id }]);
      setPrompt('');
      setEditing(false);
    } catch (err) {
      setError(reasonOf(err, 'Could not write the minutes.'));
    } finally {
      setBusy(null);
    }
  };

  const startEdit = () => { setDraft(latest?.body_md || ''); setEditing(true); setError(null); };

  const save = async () => {
    if (!latest) return;
    setBusy('save');
    setError(null);
    try {
      const m = await editMinutes(latest.id, draft);
      // The server may answer a new version, or the same one amended.
      if (m?.id && m.id !== latest.id) onChange((cur) => [...(Array.isArray(cur) ? cur : []), { state: 'draft', ...m }]);
      else replace({ ...latest, ...(m || {}), body_md: m?.body_md ?? draft, state: m?.state || 'draft' });
      setEditing(false);
    } catch (err) {
      setError(reasonOf(err, 'Could not save the minutes.'));
    } finally {
      setBusy(null);
    }
  };

  const approve = async () => {
    if (!latest) return;
    setBusy('approve');
    setError(null);
    try {
      const m = await approveMinutes(latest.id);
      replace({ ...latest, ...(m || {}), state: m?.state || 'approved' });
    } catch (err) {
      setError(reasonOf(err, 'Could not approve the minutes.'));
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    if (!latest) return;
    setBusy('send');
    setError(null);
    try {
      const r = await sendMinutes(latest.id, 'attendees');
      // The server answers with the minutes as they now are (sent, and
      // approved with it) and who got them, each with ok or a reason.
      const row = r?.minutes && typeof r.minutes === 'object' ? r.minutes : {};
      replace({ ...latest, ...row, state: row.state || 'sent', sent_at: row.sent_at || r?.sent_at || new Date().toISOString(), sent_to: r?.sent_to || null });
      setSent(Array.isArray(r?.sent_to) ? r.sent_to : null);
      setConfirming(false);
    } catch (err) {
      setError(reasonOf(err, 'Could not send the minutes.'));
    } finally {
      setBusy(null);
    }
  };

  const auto = meeting?.minutes_mode === 'auto';
  // Claude writes the minutes; with the AI off (078) the form gives way to a
  // note. Minutes already written are still read, edited, approved and sent.
  const aiOn = useFeature('ai');
  const who = recipientsOf(meeting);
  const older = list.slice(1);

  return (
    <section className="mt-minutes" aria-label="Minutes" data-panel="minutes">
      {reasonDialog}
      {error && (
        <p className="mt-error" role="alert">
          {error} <button type="button" className="tds-link" onClick={() => setError(null)}>Dismiss</button>
        </p>
      )}

      {canEdit && !aiOn && (
        <p className="rc-ai-off" role="note">
          AI minutes are switched off by your administrator, so Jarvis does not write them.
          {latest ? ' The minutes below can still be edited, approved and sent.' : ' The transcript is on the recording.'}
        </p>
      )}
      {canEdit && aiOn && (
        <form className="mt-ask" onSubmit={(e) => { e.preventDefault(); create(); }}>
          {ready.length > 1 && (
            <label className="tds-field">
              <span>From the recording</span>
              <select value={source?.id || ''} onChange={(e) => setRecId(e.target.value)} aria-label="Which recording">
                {ready.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.started_at ? relative(r.started_at) : 'Recording'}{r.started_by_name ? ` · ${r.started_by_name}` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="tds-field">
            <span>Instruction (optional)</span>
            <GrowingTextarea rows={2} maxLength={1000} value={prompt} aria-label="Instruction for the minutes"
              placeholder="e.g. decisions first, then actions with owners and dates"
              onChange={(e) => setPrompt(e.target.value)} disabled={!source || busy === 'create'} />
          </label>
          <div className="mt-ask-row">
            <button type="submit" className="btn btn-primary tds-btn mt-create" disabled={!source || busy === 'create'}>
              {busy === 'create' ? 'Writing…' : latest ? 'Write a new version' : 'Create minutes'}
            </button>
            {!source && (
              <span className="mt-quiet">
                {recordings.length ? 'Minutes need the transcript, which is not ready yet.'
                  : 'Minutes are written from a recording of the meeting, and there is none yet.'}
              </span>
            )}
            {source && auto && (
              <span className="mt-quiet">This meeting sends its minutes automatically once they are written.</span>
            )}
          </div>
        </form>
      )}

      {!latest && (
        <p className="mt-quiet mt-empty-note">
          {canEdit ? 'No minutes yet. Say what to focus on, or just press the button.'
            : 'No minutes have been shared for this meeting yet.'}
        </p>
      )}

      {latest && (
        <article className="mt-minute" data-minutes={latest.id} data-version={latest.version}>
          <header className="mt-minute-head">
            <span className="mt-minute-title">
              <Icon name="note" size={15} /> Minutes{latest.version ? ` · v${latest.version}` : ''}
            </span>
            <MinutesChip state={latest.state} />
            <span className="mt-minute-when">
              {latest.created_at ? relative(latest.created_at) : ''}
              {latest.created_by_name ? ` · ${latest.created_by_name}` : ''}
              {latest.state === 'sent' && latest.sent_at ? ` · sent ${relative(latest.sent_at)}` : ''}
            </span>
          </header>
          {latest.prompt && <p className="mt-minute-prompt">“{latest.prompt}”</p>}

          {editing ? (
            <div className="mt-edit">
              <label className="tds-field mt-edit-field">
                <span>Markdown</span>
                <textarea className="mt-edit-text" value={draft} aria-label="Edit the minutes" rows={16}
                  onChange={(e) => setDraft(e.target.value)} />
              </label>
              <div className="mt-edit-preview" aria-label="Preview">
                <span className="mt-edit-label">Preview</span>
                <Markdown text={draft} className="rc-md mt-md" />
              </div>
            </div>
          ) : (
            <Markdown text={latest.body_md || ''} className="rc-md mt-md" />
          )}

          {canEdit && (
            <div className="mt-minute-foot">
              {editing ? (
                <>
                  <button type="button" className="btn btn-outline tds-btn" disabled={busy === 'save'} onClick={() => setEditing(false)}>Cancel</button>
                  <button type="button" className="btn btn-primary tds-btn mt-save" disabled={busy === 'save'} onClick={save}>
                    {busy === 'save' ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : (
                <>
                  {latest.state !== 'sent' && (
                    <button type="button" className="btn btn-outline tds-btn mt-edit-btn" onClick={startEdit}>Edit</button>
                  )}
                  {latest.state === 'draft' && (
                    <button type="button" className="btn btn-outline tds-btn mt-approve" disabled={busy === 'approve'} onClick={approve}>
                      {busy === 'approve' ? 'Approving…' : 'Approve'}
                    </button>
                  )}
                  {latest.state !== 'sent' && (
                    <button type="button" className="btn btn-primary tds-btn mt-send" disabled={busy === 'send'}
                      onClick={() => setConfirming(true)}>
                      Send to attendees
                    </button>
                  )}
                  {latest.state === 'sent' && (
                    <span className="mt-quiet">
                      Sent{Array.isArray(latest.sent_to) && latest.sent_to.length ? ` to ${latest.sent_to.length} ${latest.sent_to.length === 1 ? 'person' : 'people'}` : ''}.
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </article>
      )}

      {older.length > 0 && (
        <div className="mt-older">
          <button type="button" className="tds-link mt-older-btn" aria-expanded={showOld} onClick={() => setShowOld((v) => !v)}>
            {showOld ? 'Hide' : 'Show'} {older.length} earlier version{older.length === 1 ? '' : 's'}
          </button>
          {showOld && (
            <ul className="mt-older-list">
              {older.map((m) => (
                <li key={m.id} className="mt-older-row" data-minutes={m.id} data-focus={String(m.id) === String(focusId) || undefined}>
                  <span className="mt-older-v">v{m.version || '?'}</span>
                  <MinutesChip state={m.state} />
                  <span className="mt-minute-when">{m.created_at ? relative(m.created_at) : ''}{m.created_by_name ? ` · ${m.created_by_name}` : ''}</span>
                  <details className="mt-older-body" open={String(m.id) === String(focusId) || undefined}>
                    <summary>Read</summary>
                    <Markdown text={m.body_md || ''} className="rc-md mt-md" />
                  </details>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {sent && (() => {
        const name = (s) => (typeof s === 'string' ? s : s.name || s.email);
        const ok = sent.filter((s) => typeof s === 'string' || s.ok !== false);
        const failed = sent.filter((s) => typeof s !== 'string' && s.ok === false);
        return (
          <p className="mt-quiet mt-sent-note" role="status">
            Sent to {ok.map(name).filter(Boolean).join(', ') || 'the attendees'}.
            {failed.length > 0 && (
              <span className="mt-sent-failed"> Could not reach {failed.map((s) => `${name(s)}${s.reason ? ` (${s.reason})` : ''}`).join(', ')}.</span>
            )}
          </p>
        );
      })()}

      {confirming && latest && (
        <Dialog label="Send these minutes?" onClose={() => setConfirming(false)} className="mt-send-dialog">
          <p className="tds-quiet">
            {latest.state === 'draft' ? 'Sending approves this version as well. ' : ''}
            Everybody below gets this version in Jarvis
            {who.guestsGet && who.guests.length ? ', and the outside guests get it by email' : ''}.
          </p>
          <ul className="mt-recipients" aria-label="Who will get the minutes">
            {who.inside.map((a) => (
              <li key={a.id || a.key} className="mt-recipient" data-recipient={a.key || a.email}>
                <Avatar name={a.name} who={a.key || a.name} />
                <span className="mt-recipient-name">{a.name}</span>
                {a.kind === 'contractor' && <span className="tds-it" title="A contractor">IT</span>}
              </li>
            ))}
            {who.guestsGet && who.guests.map((a) => (
              <li key={a.id || a.email} className="mt-recipient is-guest" data-recipient={a.email}>
                <Avatar name={a.name || a.email} who={a.email} />
                <span className="mt-recipient-name">{a.name || a.email}</span>
                <span className="mt-chip is-quiet">guest · by email</span>
              </li>
            ))}
          </ul>
          {!who.guestsGet && who.guests.length > 0 && (
            <p className="tds-quiet mt-guests-note" data-guests-kept="">
              The {who.guests.length} outside guest{who.guests.length === 1 ? '' : 's'} will not get them: the
              meeting was made without &ldquo;also email minutes to outside guests&rdquo;.
            </p>
          )}
          <div className="tds-dialog-foot">
            <button type="button" className="btn btn-outline tds-btn" disabled={busy === 'send'} onClick={() => setConfirming(false)}>Cancel</button>
            <button type="button" className="btn btn-primary tds-btn mt-send-confirm" disabled={busy === 'send'} onClick={send}>
              {busy === 'send' ? 'Sending…' : 'Send'}
            </button>
          </div>
        </Dialog>
      )}
    </section>
  );
}
